// MyVisits.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';
import QRCode from 'react-qr-code';
import StudentDetailsModal from './StudentDetailsModal';
import { FaBookOpen } from 'react-icons/fa';
import { MapModal } from './MapModal';
import { MapPin, Building2, Home } from 'lucide-react';
import { SiGooglemaps } from 'react-icons/si';
import { FiVideo, FiVideoOff, FiLink2 } from 'react-icons/fi';

// --- CANCEL VISIT MODAL ---
function CancelModal({ open, onClose, onSubmit, loading }) {
  const [reason, setReason] = useState('');
  const REASONS = [
    'Cancelled by teacher',
    'Cancelled by family',
    'Late cancellation',
    'NoShow/withAppointmentConfirmation',
  ];

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">Cancel Visit</h3>
        <p className="mb-2">Are you sure you want to cancel this visit?</p>
        <label className="block mb-1 font-semibold">Reason for cancelation:</label>
        <select
          className="border rounded px-3 py-2 w-full mb-4"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">-- Choose reason --</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
            Back
          </button>
          <button
            onClick={() => onSubmit(reason)}
            disabled={!reason || loading}
            className={`px-4 py-2 bg-purple-700 text-white rounded ${
              !reason || loading ? 'opacity-60 pointer-events-none' : ''
            }`}
          >
            {loading ? 'Canceling...' : 'Cancel Visit'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- CANCEL WORK MODAL ---
function CancelWorkModal({ open, onClose, onSubmit, loading }) {
  const [reason, setReason] = useState('');
  const REASONS = ['Schedule changed', 'Completed elsewhere', 'Other'];

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">Cancel Work</h3>
        <p className="mb-2">Are you sure you want to cancel this work block?</p>
        <label className="block mb-1 font-semibold">Reason:</label>
        <select
          className="border rounded px-3 py-2 w-full mb-4"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        >
          <option value="">-- Choose reason --</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>

        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
            Back
          </button>
          <button
            onClick={() => onSubmit(reason)}
            disabled={!reason || loading}
            className={`px-4 py-2 bg-purple-700 text-white rounded ${
              !reason || loading ? 'opacity-60 pointer-events-none' : ''
            }`}
          >
            {loading ? 'Canceling...' : 'Cancel Work'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- WORK LOCATION MODAL (re-uses MapModal) ---
function WorkLocationModal({ open, onClose, title, addressLine, dest, teacher }) {
  if (!open) return null;

  const hasCoords =
    (dest?.lat != null && dest?.lon != null) ||
    (dest?.latitude != null && dest?.longitude != null);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate">{title || 'Work location'}</h3>
            {addressLine ? <p className="text-sm text-gray-600 mt-1">{addressLine}</p> : null}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" title="Close">
            ✕
          </button>
        </div>

        {!hasCoords ? (
          <div className="p-4 text-center text-gray-500 text-sm">
            Location coordinates are missing for this work block.
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <MapModal open={open} student={dest} teacher={teacher} onClose={onClose} />
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// --- MENTOR FEEDBACK MODAL (isti kao u MyVisitsToday) ---
function MentorFeedbackModal({ open, onClose, visit }) {
  const [participation, setParticipation] = useState('observed');
  const [rating, setRating] = useState('3');
  const [mentorLevel, setMentorLevel] = useState('');
  const [suggestPromotion, setSuggestPromotion] = useState('none');
  const [flagged, setFlagged] = useState(false);
  const [notes, setNotes] = useState('');
  const [stageAtVisit, setStageAtVisit] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);

  useEffect(() => {
    if (!open) {
      setParticipation('observed');
      setRating('3');
      setMentorLevel('');
      setSuggestPromotion('none');
      setFlagged(false);
      setNotes('');
      setStageAtVisit('');
      setSaving(false);
      setLoading(false);
      setHasExisting(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !visit?.id) return;

    setLoading(true);
    setHasExisting(false);

    (async () => {
      try {
        const res = await API.get(`/training/feedback/${visit.id}`);
        if (res.data) {
          const fb = res.data;
          setHasExisting(true);

          if (fb.mentor_rating != null) setRating(String(fb.mentor_rating));
          if (fb.mentor_level) setMentorLevel(fb.mentor_level);
          if (fb.mentor_comment) setNotes(fb.mentor_comment);
          if (fb.suggest_promotion) setSuggestPromotion(fb.suggest_promotion);
          setFlagged(!!fb.is_flagged);
          if (fb.stage_at_visit) setStageAtVisit(fb.stage_at_visit);
        } else {
          setHasExisting(false);
        }
      } catch (err) {
        console.error(err);
        const msg = err?.response?.data?.error || 'Failed to load existing mentor feedback.';
        alert(msg);
        onClose();
      } finally {
        setLoading(false);
      }
    })();
  }, [open, visit, onClose]);

  if (!open || !visit) return null;

  const studentName =
    (visit.student_last_name || '') +
    (visit.student_last_name ? ', ' : '') +
    (visit.student_first_name || '');

  const timeRange = visit.scheduled_time
    ? visit.scheduled_time + (visit.event_end_time ? `–${visit.event_end_time.slice(0, 5)}` : '')
    : '';

  const handleSave = async () => {
    if (!visit?.id) return;

    const ratingNum = rating ? Number(rating) : null;
    if (rating && (Number.isNaN(ratingNum) || ratingNum < 1 || ratingNum > 5)) {
      alert('Overall rating must be between 1 and 5.');
      return;
    }

    setSaving(true);
    try {
      await API.post(`/training/feedback/${visit.id}`, {
        mentor_rating: ratingNum,
        mentor_level: mentorLevel || null,
        mentor_comment: notes || null,
        suggest_promotion: suggestPromotion || 'none',
        is_flagged: flagged ? 1 : 0,
      });

      onClose();
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.error || 'Failed to save mentor feedback.';
      alert(msg);
    } finally {
      setSaving(false);
    }
  };

  const levelLabel = (level) => {
    if (!level) return '';
    if (level === 'needs_support') return 'Needs support';
    if (level === 'improving') return 'Improving';
    if (level === 'almost_independent') return 'Almost independent';
    if (level === 'ready_for_independent') return 'Ready for independent work';
    return level;
  };

  const promotionLabel = (code) => {
    if (code === 'to_supervised') return 'Promote to supervised stage';
    if (code === 'to_independent') return 'Ready for independent work';
    return 'No promotion suggestion';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-[80vh] overflow-y-auto p-5">
        <h3 className="text-lg font-bold mb-3">{hasExisting ? 'Edit mentor feedback' : 'Mentor feedback'}</h3>

        <div className="text-sm mb-3 space-y-1">
          <div>
            <span className="font-semibold">Student: </span>
            <span>{studentName || '—'}</span>
          </div>
          <div>
            <span className="font-semibold">Visit time: </span>
            <span>{timeRange || '—'}</span>
          </div>
          <div>
            <span className="font-semibold">Trainee: </span>
            <span>#{visit.trainee_teacher_id} (trainee)</span>
          </div>
          {stageAtVisit && (
            <div>
              <span className="font-semibold">Stage during visit: </span>
              <span>{stageAtVisit}</span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-4 text-center text-gray-400 text-sm">Loading existing feedback...</div>
        ) : (
          <>
            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                Participation level (for your reference)
              </label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={participation}
                onChange={(e) => setParticipation(e.target.value)}
              >
                <option value="observed">Observed only (shadow)</option>
                <option value="co_led">Co-led with mentor</option>
                <option value="led">Led by trainee</option>
              </select>
              <p className="mt-1 text-[11px] text-gray-500">
                This value is not part of automatic promotion rules, it is just a note for you.
              </p>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Overall rating (1–5)</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={rating}
                onChange={(e) => setRating(e.target.value)}
              >
                <option value="1">1 – Needs a lot of support</option>
                <option value="2">2 – Emerging skills</option>
                <option value="3">3 – Solid / expected</option>
                <option value="4">4 – Strong performance</option>
                <option value="5">5 – Ready for independent work</option>
              </select>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Mentor level (summary)</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={mentorLevel}
                onChange={(e) => setMentorLevel(e.target.value)}
              >
                <option value="">-- Choose level --</option>
                <option value="needs_support">Needs support – requires close guidance</option>
                <option value="improving">Improving – progressing but not yet stable</option>
                <option value="almost_independent">Almost independent – needs occasional support</option>
                <option value="ready_for_independent">Ready for independent work</option>
              </select>
              {mentorLevel && (
                <p className="mt-1 text-[11px] text-gray-500">Selected level: {levelLabel(mentorLevel)}</p>
              )}
            </div>

            <div className="mb-3">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Promotion suggestion</label>
              <select
                className="w-full border rounded px-3 py-2 text-sm"
                value={suggestPromotion}
                onChange={(e) => setSuggestPromotion(e.target.value)}
              >
                <option value="none">No promotion suggestion</option>
                <option value="to_supervised">Suggest promotion to supervised stage</option>
                <option value="to_independent">Suggest promotion to independent work</option>
              </select>
              {suggestPromotion && (
                <p className="mt-1 text-[11px] text-gray-500">{promotionLabel(suggestPromotion)}</p>
              )}
            </div>

            <div className="mb-3">
              <label className="inline-flex items-center gap-2 text-xs text-red-700">
                <input
                  type="checkbox"
                  className="rounded"
                  checked={flagged}
                  onChange={(e) => setFlagged(e.target.checked)}
                />
                Flag this visit for admin review
              </label>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Mentor feedback (short summary)</label>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Key strengths, areas to improve, next steps..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm bg-gray-200 rounded hover:bg-gray-300"
                disabled={saving}
              >
                Close
              </button>
              <button
                onClick={handleSave}
                disabled={saving || loading}
                className={`px-4 py-2 text-sm rounded text-white ${
                  saving || loading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {saving ? 'Saving...' : hasExisting ? 'Update feedback' : 'Save feedback'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/** =========================================================
 * UI helpers
 * ========================================================= */

function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, 0);
  d.setMinutes(d.getMinutes() + (mins || 0));
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function VisitTimeType({ scheduled_time, event_end_time }) {
  const from = scheduled_time || '';
  let to = null;
  if (event_end_time) to = event_end_time.slice(0, 5);
  else if (from) to = addMinutesHHMM(from, 45);

  return (
    <div className="flex flex-col items-start">
      <span className="font-mono text-base">{from && to ? `${from}–${to}` : from || '-'}</span>
    </div>
  );
}

function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>{children || status}</span>;
}

function ProgramBadge({ program_type, discipline_code }) {
  const raw = (discipline_code || program_type || '').toString().trim();
  if (!raw) return null;
  const code = raw.replace(/^PROG_/i, '').toUpperCase();

  let cls = 'px-2 py-0.5 rounded text-xs';
  let extra = '';
  let label = code;

  if (code === 'PI') extra = ' bg-orange-100 text-orange-800 border-orange-200';
  else if (code === 'EI') extra = ' bg-blue-100 text-blue-800 border-blue-200';
  else if (code === 'ABA') extra = ' bg-purple-100 text-purple-800 border-purple-200';
  else if (code === 'OT') extra = ' bg-teal-100 text-teal-800 border-teal-200';
  else if (code === 'PT') extra = ' bg-emerald-100 text-emerald-800 border-emerald-200';
  else if (code === 'ST') extra = ' bg-green-100 text-green-800 border-green-200';
  else extra = ' border-gray-400 text-gray-700 bg-gray-50';

  return <span className={cls + extra}>{label}</span>;
}

function EventBadge({ isEvent }) {
  if (!isEvent) return null;
  return (
    <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800 border border-yellow-300">
      EVENT
    </span>
  );
}

function TelehealthBadge({ delivery_mode }) {
  if (delivery_mode !== 'telehealth' && delivery_mode !== 'hybrid') return null;
  const label = delivery_mode === 'telehealth' ? 'Telehealth session' : 'Hybrid (Telehealth)';
  return (
    <span className="inline-flex items-center gap-1 mt-0.5 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-sky-100 text-sky-800 border border-sky-300">
      <FiVideo className="w-3 h-3" />
      {label}
    </span>
  );
}

function TrainingBadge({ trainingType }) {
  if (!trainingType) return null;

  let label = null;
  if (trainingType === 'shadow') label = 'Shadow visit';
  else if (trainingType === 'supervised') label = 'Supervised visit';
  else if (trainingType === 'training') label = 'Supervised visit';
  if (!label) return null;

  return (
    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-teal-100 text-teal-800 border border-teal-300">
      {label}
    </span>
  );
}

function parseDateStr(v) {
  const s = String(v || '');
  const d = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}

function parseHHMM(val) {
  const s = String(val || '').trim();
  const m = s.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

function minutesFromHHMM(hhmm) {
  const t = parseHHMM(hhmm);
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function itemTimeHHMM(v) {
  return parseHHMM(v?.scheduled_time) || parseHHMM(v?.from_hhmm) || parseHHMM(v?.scheduled_at) || '';
}

function itemSortValue(v) {
  const d = parseDateStr(v?.scheduled_at);
  const hhmm = itemTimeHHMM(v);
  const mins = minutesFromHHMM(hhmm) ?? 0;

  const y = Number(d.slice(0, 4)) || 0;
  const mo = Number(d.slice(5, 7)) || 0;
  const da = Number(d.slice(8, 10)) || 0;
  const dayKey = y * 372 + mo * 31 + da;

  return dayKey * 1440 + mins;
}

function isWorkItem(v) {
  return v && (v.__kind === 'work' || v._is_work === true);
}

function sortUnified(a, b) {
  const av = itemSortValue(a);
  const bv = itemSortValue(b);
  if (av !== bv) return av - bv;

  const ak = isWorkItem(a) ? 1 : 0;
  const bk = isWorkItem(b) ? 1 : 0;
  if (ak !== bk) return ak - bk;

  return Number(a?.id || 0) - Number(b?.id || 0);
}

function groupByDate(items) {
  const acc = {};
  for (const v of items || []) {
    const date = parseDateStr(v?.scheduled_at);
    if (!date) continue;
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
  }
  Object.keys(acc).forEach((d) => acc[d].sort(sortUnified));
  return acc;
}

function buildClinicDestFromLocationRow(loc) {
  if (!loc) return null;

  const parts = [loc.street, loc.city, loc.state, loc.zip, loc.country]
    .map((x) => String(x || '').trim())
    .filter(Boolean);

  const addressLine = parts.join(', ');

  const lat = loc.latitude != null ? Number(loc.latitude) : null;
  const lon = loc.longitude != null ? Number(loc.longitude) : null;

  return {
    name: (loc.name || 'Clinic').trim(),
    address: addressLine,
    lat,
    lon,
    latitude: lat,
    longitude: lon,
  };
}

function getClinicNameForVisit(v, orgLocationsMap) {
  const mode = String(v?.location_mode || '').toLowerCase();
  if (mode !== 'at_clinic') return '';
  const id = Number(v?.org_location_id);
  if (!id) return '';
  const loc = orgLocationsMap?.[id];
  return (loc?.name || '').trim();
}

// ✅ NEW: org location name for work (same idea as AdminVisits)
function getOrgLocNameForWork(w, orgLocationsMap) {
  if (!w) return '';
  if (String(w.location_mode || '') !== 'org_location') return '';
  const id = Number(w.org_location_id);
  if (!id) return '';
  const loc = orgLocationsMap?.[id];
  return (loc?.name || '').trim();
}

function getVisitRowProps(v, serverDateStr) {
  const reassigned = !isWorkItem(v) && v.teacher_id !== v.original_teacher_id;
  const status = !isWorkItem(v) ? (v.reassigned_status || '') : '';
  const visitDateStr = String(v.scheduled_at || '').slice(0, 10);

  let rowClass = 'hover:bg-gray-50';
  let hideActions = false;

  const isCanceled = v.status === 'canceled';

  if (isCanceled) {
    rowClass += ' bg-purple-100 opacity-80';
    hideActions = true;
  } else {
    if (visitDateStr < serverDateStr && v.status === 'draft') {
      hideActions = true;
      rowClass += ' opacity-60 bg-gray-100';
    } else if (visitDateStr > serverDateStr) {
      hideActions = true;
      rowClass += ' opacity-80 bg-yellow-50';
    } else if (visitDateStr === serverDateStr) {
      rowClass += ' bg-green-50';
    }

    if (reassigned && (status === 'pending' || status === 'rejected')) {
      hideActions = true;
      rowClass += status === 'rejected' ? ' bg-red-50' : ' bg-orange-50';
    }
  }

  // work tint
  if (isWorkItem(v) && !isCanceled && !(visitDateStr < serverDateStr && v.status === 'draft')) {
    rowClass += ' bg-sky-50';
  }

  return { rowClass, hideActions, reassigned, status, visitDateStr, isCanceled };
}

const isEventVisit = (v) =>
  v.type === 'event' || v.visit_type === 'event' || v.is_event === 1 || v.is_event === true;

const isTelehealthVisit = (v) =>
  v.delivery_mode === 'telehealth' || v.delivery_mode === 'hybrid';

function toDateList(dateFrom, dateTo, hardLimitDays = 31) {
  if (!dateFrom || !dateTo) return [];
  const start = new Date(`${dateFrom}T00:00:00`);
  const end = new Date(`${dateTo}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
  if (end < start) return [];

  const days = Math.floor((end.getTime() - start.getTime()) / (24 * 3600 * 1000)) + 1;
  if (days > hardLimitDays) return null; // signal "too many"

  const pad = (n) => String(n).padStart(2, '0');
  const out = [];
  const cur = new Date(start);
  for (let i = 0; i < days; i++) {
    out.push(`${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

function normalizeTeacherWorkToUnified(rows, dateStr) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((tw) => {
    const from = tw.from_hhmm || parseHHMM(tw.scheduled_at) || '';
    const to = tw.to_hhmm || tw.end_hhmm || '';

    const scheduled_at = tw.scheduled_at || `${dateStr} ${from}:00`;

    return {
      ...tw,
      __kind: 'work',
      _is_work: true,

      scheduled_at,
      scheduled_time: from,
      event_end_time: to ? `${String(to).slice(0, 5)}:00` : null,

      // keep cancel reason unified name like admin
      canceled_reason: tw.canceled_reason || tw.cancel_reason || tw.cancelled_reason || null,

      checkin_time_local: tw.checkin_hhmm || (tw.checkin_time ? String(tw.checkin_time).slice(11, 16) : null),
      checkout_time_local: tw.checkout_hhmm || (tw.checkout_time ? String(tw.checkout_time).slice(11, 16) : null),
    };
  });
}

/** =========================================================
 * MAIN
 * ========================================================= */

function MyVisits() {
  // current teacher + training stage
  const [currentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });
  const currentTeacherId = currentUser?.id;
  const trainingStage = (currentUser?.training_stage || 'none').toString().trim().toLowerCase();

  const [visits, setVisits] = useState([]);
  const [teacherWork, setTeacherWork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState({ lat: null, lon: null });
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '' });

  const [qrModalVisitId, setQrModalVisitId] = useState(null);
  const [visitConfirmed, setVisitConfirmed] = useState(false);

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalVisit, setNotesModalVisit] = useState(null);
  const [notesEditMode, setNotesEditMode] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);

  const [domainList, setDomainList] = useState([]);
  const [domainNotes, setDomainNotes] = useState({});
  const [notesModalLoading, setNotesModalLoading] = useState(false);

  const [studentDetailsModal, setStudentDetailsModal] = useState({ open: false, student: null });
  const [serverDateStr, setServerDateStr] = useState(null);

  const [cancelVisitId, setCancelVisitId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [cancelWorkId, setCancelWorkId] = useState(null);
  const [cancelWorkLoading, setCancelWorkLoading] = useState(false);

  const [studentFilter, setStudentFilter] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);

  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState('');

  const [mapModal, setMapModal] = useState({ open: false, student: null, teacher: null });

  const [mentorFeedback, setMentorFeedback] = useState({ open: false, visit: null });

  const [orgLocationsMap, setOrgLocationsMap] = useState({});

  const [workLocModal, setWorkLocModal] = useState({ open: false, title: '', addressLine: '', dest: null, teacher: null });

  // ✅ Work notes modal (same behavior as admin)
  const [workNotesModal, setWorkNotesModal] = useState({
    open: false,
    teacherName: '',
    workType: '',
    workTitle: '',
    notes: '',
    scheduledAt: '',
  });

  // INIT: server time + geo
  useEffect(() => {
    API.get('/server-time')
      .then((res) => setServerDateStr(res.data.serverTime.slice(0, 10)))
      .catch(() => setServerDateStr(null));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        (err) => console.warn(err),
        { enableHighAccuracy: true }
      );
    }
  }, []);

  // load org locations (for clinic/work org_location fallbacks)
  useEffect(() => {
    if (!navigator.onLine) return;
    (async () => {
      try {
        const res = await API.get('/org-locations');
        const rows = res.data?.locations || [];
        const m = {};
        rows.forEach((r) => {
          if (r?.id) m[Number(r.id)] = r;
        });
        setOrgLocationsMap(m);
      } catch {
        setOrgLocationsMap({});
      }
    })();
  }, []);

  // default filter: last 15 days
  useEffect(() => {
    if (filters.dateFrom || filters.dateTo) return;
    const base = serverDateStr ? new Date(serverDateStr) : new Date();
    if (Number.isNaN(base.getTime())) return;
    const end = new Date(base);
    const start = new Date(base);
    start.setDate(start.getDate() - 15);
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setFilters({ dateFrom: fmt(start), dateTo: fmt(end) });
  }, [serverDateStr]); // eslint-disable-line react-hooks/exhaustive-deps

  const getCurrentWeekRange = (baseDateStr) => {
    const base = new Date(baseDateStr);
    const day = base.getDay() === 0 ? 7 : base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - day + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const pad = (n) => String(n).padStart(2, '0');
    const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return { monday: fmt(monday), sunday: fmt(sunday) };
  };

  const fetchTeacherWorkRange = async (dateFrom, dateTo) => {
    if (!currentTeacherId) return [];
    if (!dateFrom || !dateTo) return [];

    const res = await API.get('/teacher-work/range', {
      params: { date_from: dateFrom, date_to: dateTo, teacher_ids: String(currentTeacherId) },
    });

    return normalizeTeacherWorkToUnified(res.data || [], dateFrom);
  };

  const fetchVisits = async (custom) => {
    setLoading(true);
    setTeacherWork([]); // ✅ reset odmah (anti-ghost)

    try {
      const f = custom || filters;
      const params = {};
      if (f.dateFrom) params.date_from = f.dateFrom;
      if (f.dateTo) params.date_to = f.dateTo;

      const res = await API.get('/visits', { params });
      const data = res.data || [];
      setVisits(data);

      if (f.dateFrom && f.dateTo) {
        const wRes = await API.get('/teacher-work/range', {
          params: { date_from: f.dateFrom, date_to: f.dateTo, teacher_ids: String(currentTeacherId) },
        });
        const w = normalizeTeacherWorkToUnified(wRes.data || [], f.dateFrom);

        // optional dedupe
        const uniq = {};
        w.forEach((x) => {
          uniq[`w:${x.id}`] = x;
        });

        setTeacherWork(Object.values(uniq));
      } else {
        setTeacherWork([]); // ✅ open-ended -> nema work (da nema starih)
      }
    } catch (err) {
      console.error('Failed to load visits/work', err);
      setTeacherWork([]); // ✅
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!filters.dateFrom && !filters.dateTo) return;
    fetchVisits();
    // eslint-disable-next-line
  }, [filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    const unique = {};
    visits.forEach((v) => {
      if (!unique[v.student_id]) {
        unique[v.student_id] = {
          id: v.student_id,
          first_name: v.student_first_name,
          last_name: v.student_last_name,
        };
      }
    });
    const opts = Object.values(unique).sort((a, b) =>
      `${a.last_name || ''}${a.first_name || ''}`.localeCompare(`${b.last_name || ''}${b.first_name || ''}`)
    );
    setStudentOptions(opts);
    if (studentFilter && !unique[studentFilter]) setStudentFilter('');
  }, [visits, studentFilter]);

  const setQuickFilter = (type) => {
    if (!serverDateStr) return;

    let nf = { ...filters };

    if (type === 'today') {
      nf = { dateFrom: serverDateStr, dateTo: serverDateStr };
    } else if (type === 'week') {
      const { monday, sunday } = getCurrentWeekRange(serverDateStr);
      nf = { dateFrom: monday, dateTo: sunday };
    } else if (type === 'future') {
      const base = new Date(`${serverDateStr}T00:00:00`);
      const end = new Date(base);
      end.setDate(end.getDate() + 60); // npr. 60 dana

      const pad = (n) => String(n).padStart(2, '0');
      const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

      nf = { dateFrom: serverDateStr, dateTo: fmt(end) };
    }

    setFilters(nf);
    fetchVisits(nf);
  };

  const openMapModalFunc = async (visit) => {
    try {
      const res = await API.get('/visit-map-locations', {
        params: { student_id: visit.student_id, teacher_id: visit.teacher_id },
      });

      let studentObj = res.data.student;
      const teacherObj = res.data.teacher;

      const isClinic = String(visit.location_mode || 'at_home') === 'at_clinic';
      if (isClinic && visit.org_location_id) {
        const loc = orgLocationsMap?.[Number(visit.org_location_id)];
        const clinicDest = buildClinicDestFromLocationRow(loc);
        if (clinicDest?.lat != null && clinicDest?.lon != null) {
          studentObj = clinicDest;
        } else {
          alert('Clinic location is missing coordinates (latitude/longitude).');
        }
      }

      setMapModal({ open: true, student: studentObj, teacher: teacherObj });
    } catch {
      alert('Failed to fetch map data');
    }
  };

  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);
    try {
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  const openStudentModal = async (studentId) => {
    try {
      const res = await API.get(`/students/${studentId}/full`);
      setStudentDetailsModal({ open: true, student: res.data });
    } catch {
      alert('Failed to load student details');
    }
  };

  const handleCheckin = async (id) => {
    if (!location.lat) return alert('Geolocation required');
    try {
      await API.patch(`/visits/${id}/checkin`, location);
      fetchVisits();
    } catch {
      alert('Failed to check-in');
    }
  };

  const handleWorkCheckin = async (id) => {
    if (!location.lat) return alert('Geolocation required');
    try {
      await API.patch(`/teacher-work/${id}/checkin`, location);
      fetchVisits();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to check-in work');
    }
  };

  const handleWorkCheckout = async (id) => {
    if (!location.lat) return alert('Geolocation required');
    try {
      await API.patch(`/teacher-work/${id}/checkout`, location);
      fetchVisits();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to checkout work');
    }
  };

  // ✅ work notes modal opener (same idea as admin)
  const openWorkNotesModal = (w) => {
    const teacherName = String(currentUser?.name || 'You').trim();
    const workType = String(w?.work_type || 'other');
    setWorkNotesModal({
      open: true,
      teacherName,
      workType,
      workTitle: String(w?.title || '').trim(),
      notes: String(w?.notes || '').trim(),
      scheduledAt: String(w?.scheduled_at || ''),
    });
  };

  const openWorkLocationModal = async (tw) => {
    if (!tw) return;

    const workType = String(tw.work_type || 'other');
    let locationName = '';
    let addressLine = '';
    let lat = null;
    let lon = null;

    if (tw.location_mode === 'org_location') {
      const fallbackLoc = tw.org_location_id ? orgLocationsMap?.[Number(tw.org_location_id)] : null;

      locationName =
        String(tw.org_location_name || '').trim() ||
        String(fallbackLoc?.name || '').trim();

      const parts = [
        tw.org_location_street ?? fallbackLoc?.street,
        tw.org_location_city ?? fallbackLoc?.city,
        tw.org_location_state ?? fallbackLoc?.state,
        tw.org_location_zip ?? fallbackLoc?.zip,
        tw.org_location_country ?? fallbackLoc?.country,
      ]
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      addressLine = parts.join(', ');

      const latSrc = tw.org_location_latitude ?? fallbackLoc?.latitude;
      const lonSrc = tw.org_location_longitude ?? fallbackLoc?.longitude;

      lat = latSrc != null ? Number(latSrc) : null;
      lon = lonSrc != null ? Number(lonSrc) : null;
    } else if (tw.location_mode === 'teacher_home') {
      locationName = 'Teacher home';
      const parts = [tw.teacher_street, tw.teacher_city, tw.teacher_state, tw.teacher_zip, tw.teacher_country]
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      addressLine = parts.join(', ');
      lat = tw.teacher_lat != null ? Number(tw.teacher_lat) : null;
      lon = tw.teacher_lon != null ? Number(tw.teacher_lon) : null;
    } else if (tw.location_mode === 'remote') {
      locationName = 'Remote';
      addressLine = 'Remote work';
      lat = null;
      lon = null;
    } else {
      locationName = (tw.org_location_name || '').trim();
    }

    const title = locationName ? `Work: ${workType} - ${locationName}` : `Work: ${workType}`;

    const dest = {
      name: locationName || 'Work',
      address: addressLine,
      lat,
      lon,
      latitude: lat,
      longitude: lon,
    };

    const teacherObj = {
      name: currentUser?.name || 'You',
      lat: location.lat,
      lon: location.lon,
      latitude: location.lat,
      longitude: location.lon,
    };

    setWorkLocModal({ open: true, title, addressLine, dest, teacher: teacherObj });
  };

  // Notes modal (domains)
  const openNotesModal = async (visit, mode) => {
    setNotesModalVisit(visit);
    setNotesEditMode(mode === 'edit');
    setShowNotesModal(true);
    setNotesModalLoading(true);
    setDomainList([]);
    setDomainNotes({});

    try {
      const res = await API.get(`/visits/${visit.id}/domain-notes`);
      const domains = res.data?.domains || [];
      setDomainList(domains);

      const initial = {};
      domains.forEach((d) => {
        initial[d.id] = d.note || '';
      });
      setDomainNotes(initial);
    } catch (err) {
      console.error('Failed to load domain notes', err);
      alert('Failed to load domain notes for this visit.');
    } finally {
      setNotesModalLoading(false);
    }
  };

  const handleCheckout = (visit) => {
    openNotesModal(visit, 'finalize');
  };

  const handleSaveNotes = async () => {
    if (!notesModalVisit) return;

    const notesPayload = Object.entries(domainNotes || {}).map(([domainId, note]) => ({
      domain_id: Number(domainId),
      note: note || '',
    }));

    setNotesSaving(true);
    try {
      await API.post(`/visits/${notesModalVisit.id}/domain-notes`, { notes: notesPayload });

      if (!notesEditMode) {
        if (!location.lat) {
          alert('Geolocation required!');
          setNotesSaving(false);
          return;
        }
        await API.patch(`/visits/${notesModalVisit.id}/checkout`, location);
      }

      setShowNotesModal(false);
      setNotesModalVisit(null);
      fetchVisits();
    } catch (err) {
      console.error(err);
      alert('Failed to save notes!');
    } finally {
      setNotesSaving(false);
    }
  };

  const handleCancelVisit = async (reason) => {
    if (!reason || !cancelVisitId) return;
    setCancelLoading(true);
    try {
      await API.patch(`/visits/${cancelVisitId}/cancel`, { reason });
      setCancelVisitId(null);
      fetchVisits();
    } catch {
      alert('Failed to cancel visit!');
    } finally {
      setCancelLoading(false);
    }
  };

  const handleCancelWork = async (reason) => {
    if (!reason || !cancelWorkId) return;
    setCancelWorkLoading(true);
    try {
      await API.post(`/teacher-work/${cancelWorkId}/cancel`, { reason });
      setCancelWorkId(null);
      fetchVisits();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to cancel work!');
    } finally {
      setCancelWorkLoading(false);
    }
  };

  // QR polling
  useEffect(() => {
    let interval;
    if (qrModalVisitId && !visitConfirmed) {
      interval = setInterval(async () => {
        try {
          const res = await API.get(`/visits/${qrModalVisitId}`);
          if (res.data && res.data.g_sign_confirmed) {
            setVisitConfirmed(true);
            fetchVisits();
            clearInterval(interval);
          }
        } catch {
          // ignore
        }
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line
  }, [qrModalVisitId, visitConfirmed]);

  const handleCloseQRModal = () => {
    setQrModalVisitId(null);
    setVisitConfirmed(false);
    fetchVisits();
  };

  const canCancelVisit = (v, visitDateStr) =>
    visitDateStr >= (serverDateStr || '') && v.status === 'draft' && !v.checkin_time;

  const canCancelWork = (w, visitDateStr) =>
    visitDateStr >= (serverDateStr || '') && w.status === 'draft' && !w.checkin_time;

  const filteredVisits = studentFilter
    ? visits.filter((v) => String(v.student_id) === String(studentFilter))
    : visits;

  const unifiedItems = useMemo(() => {
    const merged = [...(filteredVisits || []), ...(teacherWork || [])];
    merged.sort(sortUnified);
    return merged;
  }, [filteredVisits, teacherWork]);

  const grouped = useMemo(() => groupByDate(unifiedItems), [unifiedItems]);

  const openGoogleMapsDirections = async (visit) => {
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isMobile = isAndroid || isIOS;
    const popup = !isMobile ? window.open('', '_blank', 'noopener,noreferrer') : null;

    const navigate = (url) => {
      if (popup && !popup.closed) popup.location.href = url;
      else window.location.href = url;
    };

    try {
      const res = await API.get('/visit-map-locations', {
        params: { student_id: visit.student_id, teacher_id: visit.teacher_id },
      });
      const s = res.data?.student || {};

      let destLat = s.lat ?? s.latitude ?? s.primary_lat;
      let destLon = s.lon ?? s.longitude ?? s.primary_lon;

      const isClinic = String(visit?.location_mode || 'at_home') === 'at_clinic';
      if (isClinic && visit?.org_location_id) {
        const loc = orgLocationsMap?.[Number(visit.org_location_id)];
        const clinicDest = buildClinicDestFromLocationRow(loc);
        if (clinicDest?.lat != null && clinicDest?.lon != null) {
          destLat = clinicDest.lat;
          destLon = clinicDest.lon;
        } else {
          alert('Clinic location is missing coordinates (latitude/longitude).');
          if (popup && !popup.closed) popup.close();
          return;
        }
      }

      if (destLat == null || destLon == null) {
        alert('Destination location is missing.');
        if (popup && !popup.closed) popup.close();
        return;
      }

      const origin = location && location.lat && location.lon ? `${location.lat},${location.lon}` : '';
      const dest = `${destLat},${destLon}`;

      const webUrl = origin
        ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(
            dest
          )}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}&travelmode=driving`;

      if (isAndroid) {
        const navUrl = `google.navigation:q=${encodeURIComponent(dest)}&mode=d`;
        navigate(navUrl);
        setTimeout(() => navigate(webUrl), 900);
        return;
      }

      if (isIOS) {
        const iosAppUrl = origin
          ? `comgooglemaps://?saddr=${encodeURIComponent(origin)}&daddr=${encodeURIComponent(dest)}&directionsmode=driving`
          : `comgooglemaps://?daddr=${encodeURIComponent(dest)}&directionsmode=driving`;

        navigate(iosAppUrl);
        setTimeout(() => navigate(webUrl), 1300);
        return;
      }

      navigate(webUrl);
    } catch (e) {
      console.error(e);
      alert('Failed to open directions.');
      if (popup && !popup.closed) popup.close();
    }
  };

  // RENDER
  return (
    <div className="w-full px-0 sm:px-1">
      <h2 className="text-lg font-bold mb-4">My Visits</h2>

      {/* FILTERS */}
      <div className="w-full bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 flex flex-col gap-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col">
            <label className="text-xs mb-1">Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              className="border p-2 rounded"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              className="border p-2 rounded"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-1 flex-wrap">
          <button onClick={() => setQuickFilter('today')} className="text-xs bg-gray-200 rounded px-2 py-1">
            Today
          </button>
          <button onClick={() => setQuickFilter('week')} className="text-xs bg-gray-200 rounded px-2 py-1">
            This Week
          </button>
          <button onClick={() => setQuickFilter('future')} className="text-xs bg-gray-200 rounded px-2 py-1">
            All Future
          </button>
        </div>
        {studentOptions.length > 0 && (
          <div className="flex flex-col mt-2 max-w-xs">
            <label className="text-xs mb-1">Filter by Student</label>
            <select className="border p-2 rounded" value={studentFilter} onChange={(e) => setStudentFilter(e.target.value)}>
              <option value="">-- All students --</option>
              {studentOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {(s.last_name || '') + (s.last_name ? ', ' : '') + (s.first_name || '')}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!serverDateStr ? (
        <div className="p-4 text-gray-500 text-center">Loading server time...</div>
      ) : loading ? (
        <div className="p-6 text-gray-400 text-center">Loading...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-gray-500 mt-6">No visits found for the selected criteria.</div>
      ) : (
        Object.entries(grouped)
          .sort(([a], [b]) => b.localeCompare(a)) // ✅ newest first
          .map(([date, group]) => (
            <div key={date} className="mb-6 shadow rounded-lg bg-white border">
              <div className="font-bold text-base px-4 py-3 bg-gray-50 rounded-t-lg flex items-center">
                <span>{date}</span>
                <span className="ml-3 bg-blue-200 text-blue-900 text-xs px-2 py-0.5 rounded-full">{group.length} items</span>
              </div>

              {/* DESKTOP */}
              <div className="overflow-x-auto hidden sm:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-2 py-1">Student / Work</th>
                      <th className="px-2 py-1">Time</th>
                      <th className="px-2 py-1 w-40">Status</th>
                      <th className="px-2 py-1">Check-in</th>
                      <th className="px-2 py-1">Checkout</th>
                      <th className="px-2 py-1">G-sign</th>
                      <th className="px-2 py-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.map((v) => {
                      const work = isWorkItem(v);
                      const { rowClass, hideActions, reassigned, status, visitDateStr, isCanceled } = getVisitRowProps(v, serverDateStr);

                      // WORK ROW
                      if (work) {
                        const from = v.scheduled_time || parseHHMM(v.from_hhmm) || parseHHMM(v.scheduled_at);
                        const to =
                          v.to_hhmm ||
                          parseHHMM(v.end_time) ||
                          (v.event_end_time ? String(v.event_end_time).slice(0, 5) : '');
                        const workType = String(v.work_type || 'other');

                        // ✅ match AdminVisits style: title is "Work: {type}", location shown below for org_location
                        const titleText = `Work: ${workType}`;
                        const orgLocName = getOrgLocNameForWork(v, orgLocationsMap);

                        const hasCheckin = !!v.checkin_time;
                        const hasCheckout = !!v.checkout_time;

                        let checkinContent = null;
                        let checkoutContent = null;
                        const gsignContent = <span className="text-xs text-gray-400">N/A</span>;
                        let actionsContent = null;

                        // locked states
                        if (isCanceled || (visitDateStr < serverDateStr && v.status === 'draft')) {
                          const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                          checkinContent = exp;
                          checkoutContent = exp;
                        } else if (visitDateStr > serverDateStr) {
                          const wait = <span className="italic text-xs text-yellow-500">Waiting</span>;
                          checkinContent = wait;
                          checkoutContent = wait;
                        }

                        if (!checkinContent) {
                          if (hasCheckin) {
                            const timeText =
                              v.checkin_time_local || (v.checkin_time ? String(v.checkin_time).slice(11, 16) : '');
                            checkinContent = <span className="text-sm">{timeText || ''}</span>;
                          } else if (!hideActions) {
                            checkinContent = (
                              <button
                                onClick={() => handleWorkCheckin(v.id)}
                                className="bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded text-xs"
                              >
                                Check-in
                              </button>
                            );
                          }
                        }

                        if (!checkoutContent) {
                          if (hasCheckout) {
                            const timeText =
                              v.checkout_time_local || (v.checkout_time ? String(v.checkout_time).slice(11, 16) : '');
                            checkoutContent = <span className="text-sm">{timeText || ''}</span>;
                          } else if (!hideActions && hasCheckin) {
                            checkoutContent = (
                              <button
                                onClick={() => handleWorkCheckout(v.id)}
                                className="bg-green-500 hover:bg-green-600 text-white px-2 py-1 rounded text-xs"
                              >
                                Checkout
                              </button>
                            );
                          }
                        }

                        if (!hideActions && canCancelWork(v, visitDateStr)) {
                          actionsContent = (
                            <div className="flex flex-col gap-1">
                              <button
                                onClick={() => setCancelWorkId(v.id)}
                                className="bg-purple-700 hover:bg-purple-800 text-white px-2 py-1 rounded text-xs"
                              >
                                Cancel
                              </button>
                            </div>
                          );
                        }

                        const workCanceledReason = String(v.canceled_reason || '').trim();

                        return (
                          <tr key={`work_${v.id}`} className={rowClass}>
                            <td className="px-2 py-1">
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Building2 className="w-4 h-4 text-slate-600" />
                                  <span className="font-semibold text-sky-900">{titleText}</span>

                                  {/* ✅ book icon -> work notes modal */}
                                  <button
                                    onClick={() => openWorkNotesModal(v)}
                                    className="text-purple-700 hover:text-purple-900"
                                    title="Show work notes"
                                  >
                                    <FaBookOpen size={18} />
                                  </button>

                                  {/* ✅ map pin -> work location modal */}
                                  <button
                                    onClick={() => openWorkLocationModal(v)}
                                    className="text-gray-400 hover:text-blue-600"
                                    title="Show work location on map"
                                  >
                                    <MapPin size={18} />
                                  </button>
                                </div>

                                {/* ✅ location below title (only for org_location) */}
                                {String(v.location_mode || '') === 'org_location' ? (
                                  <div className="mt-0.5 text-[11px] text-slate-500">
                                    {orgLocName ? (
                                      <>
                                        <span className="font-semibold">Location:</span> {orgLocName}
                                      </>
                                    ) : (
                                      <span className="italic">Location</span>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            </td>

                            <td className="px-2 py-1">
                              <VisitTimeType
                                scheduled_time={from}
                                event_end_time={to ? `${String(to).slice(0, 5)}:00` : null}
                              />
                            </td>

                            <td className="px-2 py-1 flex flex-col">
                              <StatusBadge status={v.status}>{v.status === 'canceled' ? 'Canceled' : v.status}</StatusBadge>
                              {v.status === 'canceled' && workCanceledReason ? (
                                <span className="mt-1 text-xs text-purple-700 italic">{workCanceledReason}</span>
                              ) : null}
                            </td>

                            <td className="px-2 py-1">{checkinContent}</td>
                            <td className="px-2 py-1">{checkoutContent}</td>
                            <td className="px-2 py-1">{gsignContent}</td>
                            <td className="px-2 py-1">{actionsContent}</td>
                          </tr>
                        );
                      }

                      // VISIT ROW
                      const studentName = `${v.student_last_name || ''}${v.student_last_name ? ', ' : ''}${v.student_first_name || ''}`.trim();
                      const eventFlag = isEventVisit(v);

                      const isTraineeVisitForCurrentTeacher =
                        v.trainee_teacher_id && currentTeacherId && Number(v.trainee_teacher_id) === Number(currentTeacherId);

                      const isSupervisorVisitForCurrentTeacher =
                        !isTraineeVisitForCurrentTeacher && v.trainee_teacher_id && currentTeacherId && Number(v.teacher_id) === Number(currentTeacherId);

                      const isTrainingVisitForCurrentTeacher = isTraineeVisitForCurrentTeacher || isSupervisorVisitForCurrentTeacher;

                      const isReadOnlyTrainingVisit =
                        isTraineeVisitForCurrentTeacher && (trainingStage === 'shadow' || trainingStage === 'supervised');

                      let trainingType = null;
                      if (isTraineeVisitForCurrentTeacher && (trainingStage === 'shadow' || trainingStage === 'supervised')) {
                        trainingType = trainingStage;
                      } else if (isSupervisorVisitForCurrentTeacher && v.trainee_teacher_id) {
                        trainingType = 'training';
                      }

                      const hasCheckin = !!v.checkin_time;
                      const hasCheckout = !!v.checkout_time;
                      const telehealth = isTelehealthVisit(v);

                      const hideActionsForRole = hideActions || isReadOnlyTrainingVisit;
                      const canCancelThis = !hideActionsForRole && canCancelVisit(v, visitDateStr);

                      let checkinContent = null;
                      let checkoutContent = null;
                      let gsignContent = null;
                      let editBtnsContent = null;

                      if (isCanceled || (visitDateStr < serverDateStr && v.status === 'draft')) {
                        const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                        checkinContent = exp;
                        checkoutContent = exp;
                        gsignContent = exp;
                      } else if (visitDateStr > serverDateStr) {
                        const wait = <span className="italic text-xs text-yellow-500">Waiting</span>;
                        checkinContent = wait;
                        checkoutContent = wait;
                        gsignContent = wait;
                      }

                      // check-in
                      if (!checkinContent) {
                        if (hasCheckin) {
                          if (telehealth) {
                            const timeText = v.checkin_time_local || (v.checkin_time ? v.checkin_time.slice(11, 16) : '');
                            checkinContent = (
                              <div className="flex flex-col items-start gap-1">
                                <span className="text-xs text-sky-800 font-semibold">Started at {timeText}</span>
                                {v.telehealth_link && (
                                  <a
                                    href={v.telehealth_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 underline"
                                  >
                                    <FiLink2 className="w-3 h-3" />
                                    Open telehealth
                                  </a>
                                )}
                              </div>
                            );
                          } else {
                            checkinContent = v.checkin_time_local || <span className="text-xs text-green-700">Checked in</span>;
                          }
                        } else if (!hideActionsForRole) {
                          if (telehealth) {
                            checkinContent = (
                              <button
                                onClick={() => handleCheckin(v.id)}
                                className="inline-flex items-center gap-1 bg-sky-500 hover:bg-sky-600 text-white px-2 py-1 rounded text-xs"
                              >
                                <FiVideo className="w-3 h-3" />
                                Start Telehealth
                              </button>
                            );
                          } else {
                            checkinContent = (
                              <button onClick={() => handleCheckin(v.id)} className="bg-blue-500 text-white px-2 py-1 rounded text-xs">
                                Check-in
                              </button>
                            );
                          }
                        } else if (isReadOnlyTrainingVisit) {
                          checkinContent = <span className="text-xs text-teal-700">Training visit – supervisor handles check-in</span>;
                        }
                      }

                      // checkout
                      if (!checkoutContent) {
                        if (hasCheckout) {
                          if (telehealth) {
                            const timeText = v.checkout_time_local || (v.checkout_time ? v.checkout_time.slice(11, 16) : '');
                            checkoutContent = <span className="text-xs text-sky-800">Ended at {timeText}</span>;
                          } else {
                            checkoutContent = v.checkout_time_local || <span className="text-xs text-green-700">Checked out</span>;
                          }
                        } else if (!hideActionsForRole && hasCheckin) {
                          if (telehealth) {
                            checkoutContent = (
                              <button
                                onClick={() => handleCheckout(v)}
                                className="inline-flex items-center gap-1 bg-sky-500 hover:bg-sky-600 text-white px-2 py-1 rounded text-xs"
                              >
                                <FiVideoOff className="w-3 h-3" />
                                End Telehealth
                              </button>
                            );
                          } else {
                            checkoutContent = (
                              <button onClick={() => handleCheckout(v)} className="bg-green-500 text-white px-2 py-1 rounded text-xs">
                                Checkout
                              </button>
                            );
                          }
                        } else if (isReadOnlyTrainingVisit && hasCheckin) {
                          checkoutContent = <span className="text-xs text-teal-700">Training visit – supervisor completes checkout</span>;
                        }
                      }

                      // g-sign
                      if (!gsignContent && !hideActionsForRole) {
                        gsignContent = telehealth ? <span className="text-xs text-gray-400">N/A</span> : v.g_sign_confirmed ? '✅' : '❌';
                      } else if (!gsignContent && isReadOnlyTrainingVisit) {
                        gsignContent = <span className="text-xs text-teal-700">Training visit – G-sign by supervisor</span>;
                      }

                      // actions
                      if (!hideActionsForRole || canCancelThis) {
                        const canShowQR =
                          !telehealth &&
                          !v.g_sign_confirmed &&
                          visitDateStr === serverDateStr &&
                          !isCanceled &&
                          v.status === 'draft' &&
                          !isReadOnlyTrainingVisit;

                        const canEditNotes = !hideActionsForRole && (hasCheckin || hasCheckout);

                        editBtnsContent = (
                          <div className="flex flex-col gap-1">
                            {canEditNotes && (
                              <button onClick={() => openNotesModal(v, 'edit')} className="bg-yellow-500 text-white px-2 py-1 rounded text-xs">
                                Edit Notes
                              </button>
                            )}
                            {canShowQR && (
                              <button
                                onClick={() => {
                                  setQrModalVisitId(v.id);
                                  setVisitConfirmed(false);
                                }}
                                className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                              >
                                Show QR
                              </button>
                            )}
                            {canCancelThis && (
                              <button onClick={() => setCancelVisitId(v.id)} className="bg-purple-700 text-white px-2 py-1 rounded text-xs">
                                Cancel
                              </button>
                            )}
                          </div>
                        );
                      }

                      const locMode = String(v.location_mode || 'at_home');
                      const locIcon =
                        locMode === 'at_clinic' ? (
                          <Building2 className="w-4 h-4 text-slate-600" />
                        ) : (
                          <Home className="w-4 h-4 text-slate-600" />
                        );

                      const clinicName = getClinicNameForVisit(v, orgLocationsMap);

                      return (
                        <tr key={v.id} className={rowClass}>
                          <td className="px-2 py-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-1">{locIcon}</span>

                              <span
                                onClick={() => openStudentModal(v.student_id)}
                                className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                              >
                                {studentName}
                              </span>

                              {eventFlag ? (
                                <EventBadge isEvent={true} />
                              ) : (
                                <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                              )}

                              <button
                                onClick={() => openMapModalFunc(v)}
                                className="ml-1 text-gray-400 hover:text-blue-600"
                                title="Show on Map"
                              >
                                <MapPin size={16} />
                              </button>

                              <button
                                onClick={() => openGoogleMapsDirections(v)}
                                className="ml-1 text-green-600 hover:text-green-800"
                                title="Open Google Maps directions"
                              >
                                <SiGooglemaps size={16} />
                              </button>

                              <button
                                onClick={() => openNotesHistoryModal(v.student_id, studentName)}
                                className="ml-1 text-purple-700 hover:text-purple-900"
                                title="Show Notes History"
                              >
                                <FaBookOpen size={16} />
                              </button>
                            </div>

                            {locMode === 'at_clinic' ? (
                              <div className="mt-0.5 text-[11px] text-slate-500">
                                {clinicName ? (
                                  <>
                                    <span className="font-semibold">Clinic:</span> {clinicName}
                                  </>
                                ) : (
                                  <span className="italic">Clinic</span>
                                )}
                              </div>
                            ) : null}

                            <TelehealthBadge delivery_mode={v.delivery_mode} />
                          </td>

                          <td className="px-2 py-1">
                            <VisitTimeType scheduled_time={v.scheduled_time} event_end_time={v.event_end_time} />
                          </td>

                          <td className="px-2 py-1 flex flex-col">
                            <StatusBadge status={v.status}>{v.status === 'canceled' ? 'Canceled' : v.status}</StatusBadge>

                            {isTrainingVisitForCurrentTeacher && <TrainingBadge trainingType={trainingType} />}

                            {isSupervisorVisitForCurrentTeacher && v.trainee_teacher_id && (
                              <button
                                type="button"
                                onClick={() => setMentorFeedback({ open: true, visit: v })}
                                className="mt-1 self-start px-2 py-0.5 rounded text-[11px] bg-blue-50 text-blue-700 border border-blue-300 hover:bg-blue-100"
                              >
                                Mentor feedback
                              </button>
                            )}

                            {isCanceled && v.notes && <span className="mt-1 text-xs text-purple-700 italic">{v.notes}</span>}

                            {reassigned && status && !isCanceled && (
                              <span className={`mt-1 text-xs ${status === 'rejected' ? 'text-red-700' : 'text-orange-700'}`}>
                                Substitute: {status}
                              </span>
                            )}
                          </td>

                          <td className="px-2 py-1">{checkinContent}</td>
                          <td className="px-2 py-1">{checkoutContent}</td>
                          <td className="px-2 py-1">{gsignContent}</td>
                          <td className="px-2 py-1">{editBtnsContent}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE */}
              <div className="sm:hidden flex flex-col gap-2 p-2">
                {group.map((v) => {
                  const work = isWorkItem(v);
                  const { rowClass, hideActions, reassigned, status, visitDateStr, isCanceled } = getVisitRowProps(v, serverDateStr);

                  // WORK mobile card
                  if (work) {
                    const from = v.scheduled_time || parseHHMM(v.from_hhmm) || parseHHMM(v.scheduled_at);
                    const to =
                      v.to_hhmm ||
                      parseHHMM(v.end_time) ||
                      (v.event_end_time ? String(v.event_end_time).slice(0, 5) : '');
                    const workType = String(v.work_type || 'other');

                    // ✅ match AdminVisits: "Work: {type}" + location below if org_location
                    const titleText = `Work: ${workType}`;
                    const orgLocName = getOrgLocNameForWork(v, orgLocationsMap);

                    const hasCheckin = !!v.checkin_time;
                    const hasCheckout = !!v.checkout_time;

                    let checkinContent = null;
                    let checkoutContent = null;

                    if (isCanceled || (visitDateStr < serverDateStr && v.status === 'draft')) {
                      const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                      checkinContent = exp;
                      checkoutContent = exp;
                    } else if (visitDateStr > serverDateStr) {
                      const wait = <span className="italic text-xs text-yellow-500">Waiting</span>;
                      checkinContent = wait;
                      checkoutContent = wait;
                    }

                    if (!checkinContent) {
                      if (hasCheckin) {
                        const timeText =
                          v.checkin_time_local || (v.checkin_time ? String(v.checkin_time).slice(11, 16) : '');
                        checkinContent = <span className="text-base text-slate-900 leading-none">{timeText || ''}</span>;
                      } else if (!hideActions) {
                        checkinContent = (
                          <button
                            onClick={() => handleWorkCheckin(v.id)}
                            className="bg-blue-500 text-white px-2 py-1 rounded text-xs w-full"
                          >
                            Check-in
                          </button>
                        );
                      }
                    }

                    if (!checkoutContent) {
                      if (hasCheckout) {
                        const timeText =
                          v.checkout_time_local || (v.checkout_time ? String(v.checkout_time).slice(11, 16) : '');
                        checkoutContent = <span className="text-base text-slate-900 leading-none">{timeText || ''}</span>;
                      } else if (!hideActions && hasCheckin) {
                        checkoutContent = (
                          <button
                            onClick={() => handleWorkCheckout(v.id)}
                            className="bg-green-500 text-white px-2 py-1 rounded text-xs w-full"
                          >
                            Checkout
                          </button>
                        );
                      }
                    }

                    const canCancelThisWork = !hideActions && canCancelWork(v, visitDateStr);
                    const workCanceledReason = String(v.canceled_reason || '').trim();

                    return (
                      <div key={`work_${v.id}`} className={`rounded-2xl border shadow p-3 mb-1 ${rowClass}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap min-w-0">
                              <Building2 className="w-4 h-4 text-slate-600 shrink-0" />
                              <span className="font-semibold text-sky-900 truncate">{titleText}</span>

                              <button
                                onClick={() => openWorkNotesModal(v)}
                                className="text-purple-700 hover:text-purple-900"
                                title="Show work notes"
                              >
                                <FaBookOpen size={16} />
                              </button>

                              <button
                                onClick={() => openWorkLocationModal(v)}
                                className="text-gray-400 hover:text-blue-600"
                                title="Show work location on map"
                              >
                                <MapPin size={16} />
                              </button>
                            </div>

                            {String(v.location_mode || '') === 'org_location' ? (
                              <div className="mt-1 text-[11px] text-slate-500">
                                {orgLocName ? (
                                  <>
                                    <span className="font-semibold">Location:</span> {orgLocName}
                                  </>
                                ) : (
                                  <span className="italic">Location</span>
                                )}
                              </div>
                            ) : null}

                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                              <StatusBadge status={v.status}>{v.status === 'canceled' ? 'Canceled' : v.status}</StatusBadge>
                              <span className="text-xs text-gray-400">G-sign: N/A</span>
                            </div>

                            {v.status === 'canceled' && workCanceledReason ? (
                              <div className="mt-2 text-xs text-purple-700 italic">{workCanceledReason}</div>
                            ) : null}
                          </div>

                          <div className="shrink-0 text-right">
                            <VisitTimeType
                              scheduled_time={from}
                              event_end_time={to ? `${String(to).slice(0, 5)}:00` : null}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mt-3">
                          <div>
                            <span className="block text-xs text-gray-600">Check-in</span>
                            {checkinContent}
                          </div>
                          <div>
                            <span className="block text-xs text-gray-600">Checkout</span>
                            {checkoutContent || <span className="text-xs text-slate-300">—</span>}
                          </div>
                          <div>
                            <span className="block text-xs text-gray-600">G-sign</span>
                            <span className="text-base leading-none text-gray-400">N/A</span>
                          </div>
                          <div className="flex flex-col gap-1 mt-2">
                            {canCancelThisWork ? (
                              <button
                                onClick={() => setCancelWorkId(v.id)}
                                className="bg-purple-700 text-white px-2 py-1 rounded text-xs w-full"
                              >
                                Cancel
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // VISIT mobile card (existing style, with clinic name + location icon)
                  const studentName = `${v.student_last_name || ''}${v.student_last_name ? ', ' : ''}${v.student_first_name || ''}`.trim();
                  const eventFlag = isEventVisit(v);

                  const isTraineeVisitForCurrentTeacher =
                    v.trainee_teacher_id && currentTeacherId && Number(v.trainee_teacher_id) === Number(currentTeacherId);

                  const isSupervisorVisitForCurrentTeacher =
                    !isTraineeVisitForCurrentTeacher && v.trainee_teacher_id && currentTeacherId && Number(v.teacher_id) === Number(currentTeacherId);

                  const isTrainingVisitForCurrentTeacher = isTraineeVisitForCurrentTeacher || isSupervisorVisitForCurrentTeacher;

                  const isReadOnlyTrainingVisit =
                    isTraineeVisitForCurrentTeacher && (trainingStage === 'shadow' || trainingStage === 'supervised');

                  let trainingType = null;
                  if (isTraineeVisitForCurrentTeacher && (trainingStage === 'shadow' || trainingStage === 'supervised')) {
                    trainingType = trainingStage;
                  } else if (isSupervisorVisitForCurrentTeacher && v.trainee_teacher_id) {
                    trainingType = 'training';
                  }

                  const hasCheckin = !!v.checkin_time;
                  const hasCheckout = !!v.checkout_time;
                  const telehealth = isTelehealthVisit(v);

                  const hideActionsForRole = hideActions || isReadOnlyTrainingVisit;
                  const canCancelThis = !hideActionsForRole && canCancelVisit(v, visitDateStr);

                  let checkinContent = null;
                  let checkoutContent = null;
                  let gsignContent = null;
                  let editBtnsContent = null;

                  if (isCanceled || (visitDateStr < serverDateStr && v.status === 'draft')) {
                    const exp = <span className="italic text-xs text-gray-400">Expired</span>;
                    checkinContent = exp;
                    checkoutContent = exp;
                    gsignContent = exp;
                  } else if (visitDateStr > serverDateStr) {
                    const wait = <span className="italic text-xs text-yellow-500">Waiting</span>;
                    checkinContent = wait;
                    checkoutContent = wait;
                    gsignContent = wait;
                  }

                  if (!checkinContent) {
                    if (hasCheckin) {
                      if (telehealth) {
                        const timeText = v.checkin_time_local || (v.checkin_time ? v.checkin_time.slice(11, 16) : '');
                        checkinContent = (
                          <div className="flex flex-col items-start gap-1">
                            <span className="text-xs text-sky-800 font-semibold">Started at {timeText}</span>
                            {v.telehealth_link && (
                              <a
                                href={v.telehealth_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 underline"
                              >
                                <FiLink2 className="w-3 h-3" />
                                Open telehealth
                              </a>
                            )}
                          </div>
                        );
                      } else {
                        checkinContent = v.checkin_time_local ? (
                          <span className="text-base text-slate-900 leading-none">{v.checkin_time_local}</span>
                        ) : (
                          <span className="text-xs text-green-700">Checked in</span>
                        );
                      }
                    } else if (!hideActionsForRole) {
                      checkinContent = telehealth ? (
                        <button
                          onClick={() => handleCheckin(v.id)}
                          className="inline-flex items-center justify-center gap-1 bg-sky-500 hover:bg-sky-600 text-white px-2 py-1 rounded text-xs w-full"
                        >
                          <FiVideo className="w-3 h-3" />
                          Start Telehealth
                        </button>
                      ) : (
                        <button onClick={() => handleCheckin(v.id)} className="bg-blue-500 text-white px-2 py-1 rounded text-xs w-full">
                          Check-in
                        </button>
                      );
                    } else if (isReadOnlyTrainingVisit) {
                      checkinContent = <span className="text-xs text-teal-700">Training visit – supervisor handles check-in</span>;
                    }
                  }

                  if (!checkoutContent) {
                    if (hasCheckout) {
                      if (telehealth) {
                        const timeText = v.checkout_time_local || (v.checkout_time ? v.checkout_time.slice(11, 16) : '');
                        checkoutContent = <span className="text-xs text-sky-800">Ended at {timeText}</span>;
                      } else {
                        checkoutContent = v.checkout_time_local ? (
                          <span className="text-base text-slate-900 leading-none">{v.checkout_time_local}</span>
                        ) : (
                          <span className="text-xs text-green-700">Checked out</span>
                        );
                      }
                    } else if (!hideActionsForRole && hasCheckin) {
                      checkoutContent = telehealth ? (
                        <button
                          onClick={() => handleCheckout(v)}
                          className="inline-flex items-center justify-center gap-1 bg-sky-500 hover:bg-sky-600 text-white px-2 py-1 rounded text-xs w-full"
                        >
                          <FiVideoOff className="w-3 h-3" />
                          End Telehealth
                        </button>
                      ) : (
                        <button onClick={() => handleCheckout(v)} className="bg-green-500 text-white px-2 py-1 rounded text-xs w-full">
                          Checkout
                        </button>
                      );
                    } else if (isReadOnlyTrainingVisit && hasCheckin) {
                      checkoutContent = <span className="text-xs text-teal-700">Training visit – supervisor completes checkout</span>;
                    }
                  }

                  if (!gsignContent && !hideActionsForRole) {
                    gsignContent = telehealth ? <span className="text-xs text-gray-400">N/A</span> : v.g_sign_confirmed ? '✅' : '❌';
                  } else if (!gsignContent && isReadOnlyTrainingVisit) {
                    gsignContent = <span className="text-xs text-teal-700">Training visit – G-sign by supervisor</span>;
                  }

                  if (!hideActionsForRole || canCancelThis) {
                    const canShowQR =
                      !telehealth &&
                      !v.g_sign_confirmed &&
                      visitDateStr === serverDateStr &&
                      !isCanceled &&
                      v.status === 'draft' &&
                      !isReadOnlyTrainingVisit;

                    const canEditNotes = !hideActionsForRole && (hasCheckin || hasCheckout);

                    editBtnsContent = (
                      <div className="flex flex-col gap-1">
                        {canEditNotes && (
                          <button onClick={() => openNotesModal(v, 'edit')} className="bg-yellow-500 text-white px-2 py-1 rounded text-xs w-full">
                            Edit Notes
                          </button>
                        )}
                        {canShowQR && (
                          <button
                            onClick={() => {
                              setQrModalVisitId(v.id);
                              setVisitConfirmed(false);
                            }}
                            className="bg-gray-500 text-white px-2 py-1 rounded text-xs w-full"
                          >
                            Show QR
                          </button>
                        )}
                        {canCancelThis && (
                          <button onClick={() => setCancelVisitId(v.id)} className="bg-purple-700 text-white px-2 py-1 rounded text-xs w-full">
                            Cancel
                          </button>
                        )}
                      </div>
                    );
                  }

                  const locMode = String(v.location_mode || 'at_home');
                  const locIcon =
                    locMode === 'at_clinic' ? (
                      <Building2 className="w-4 h-4 text-slate-600" />
                    ) : (
                      <Home className="w-4 h-4 text-slate-600" />
                    );

                  const clinicName = getClinicNameForVisit(v, orgLocationsMap);

                  return (
                    <div key={v.id} className={`rounded-2xl border shadow p-3 mb-1 ${rowClass}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 flex flex-col gap-1">
                          <div className="flex items-center gap-1 flex-wrap">
                            <span className="inline-flex items-center gap-1">{locIcon}</span>

                            <span
                              onClick={() => openStudentModal(v.student_id)}
                              className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                            >
                              {studentName}
                            </span>

                            {eventFlag ? (
                              <EventBadge isEvent={true} />
                            ) : (
                              <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                            )}

                            <button
                              onClick={() => openMapModalFunc(v)}
                              className="ml-1 text-gray-400 hover:text-blue-600"
                              title="Show on Map"
                            >
                              <MapPin size={16} />
                            </button>

                            <button
                              onClick={() => openGoogleMapsDirections(v)}
                              className="ml-1 text-green-600 hover:text-green-800"
                              title="Open Google Maps directions"
                            >
                              <SiGooglemaps size={16} />
                            </button>

                            <button
                              onClick={() => openNotesHistoryModal(v.student_id, studentName)}
                              className="ml-1 text-purple-700 hover:text-purple-900"
                              title="Show Notes History"
                            >
                              <FaBookOpen size={16} />
                            </button>
                          </div>

                          {locMode === 'at_clinic' ? (
                            <div className="text-[11px] text-slate-500">
                              {clinicName ? (
                                <>
                                  <span className="font-semibold">Clinic:</span> {clinicName}
                                </>
                              ) : (
                                <span className="italic">Clinic</span>
                              )}
                            </div>
                          ) : null}

                          <TelehealthBadge delivery_mode={v.delivery_mode} />
                        </div>

                        <div className="flex-1 text-right">
                          <VisitTimeType scheduled_time={v.scheduled_time} event_end_time={v.event_end_time} />
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 items-center mt-2">
                        <StatusBadge status={v.status}>{v.status === 'canceled' ? 'Canceled' : v.status}</StatusBadge>

                        {isTrainingVisitForCurrentTeacher && <TrainingBadge trainingType={trainingType} />}

                        {isSupervisorVisitForCurrentTeacher && v.trainee_teacher_id && (
                          <button
                            type="button"
                            onClick={() => setMentorFeedback({ open: true, visit: v })}
                            className="px-2 py-0.5 rounded text-[11px] bg-blue-50 text-blue-700 border border-blue-300 hover:bg-blue-100"
                          >
                            Mentor feedback
                          </button>
                        )}

                        {v.status === 'canceled' && v.notes && <span className="text-xs text-purple-700 italic">{v.notes}</span>}

                        {reassigned && status && !isCanceled && (
                          <span className={`text-xs ${status === 'rejected' ? 'text-red-700' : 'text-orange-700'}`}>
                            Substitute: {status}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                          <span className="block text-xs text-gray-600">{telehealth ? 'Session start' : 'Check-in'}</span>
                          {checkinContent}
                        </div>
                        <div>
                          <span className="block text-xs text-gray-600">{telehealth ? 'Session end' : 'Checkout'}</span>
                          {checkoutContent}
                        </div>
                        <div>
                          <span className="block text-xs text-gray-600">G-sign</span>
                          {gsignContent}
                        </div>
                        <div className="flex flex-col gap-1 mt-2">{editBtnsContent}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
      )}

      {/* Notes modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">{notesEditMode ? 'Edit Visit Notes' : 'Enter Visit Notes (per domain)'}</h3>

            {notesModalLoading ? (
              <div className="p-4 text-center text-gray-400">Loading domains...</div>
            ) : domainList.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No domains configured for this visit's discipline.</div>
            ) : (
              domainList.map((d) => (
                <div key={d.id} className="mb-3">
                  <label className="block text-sm font-medium mb-1">{d.name || d.code || `Domain #${d.id}`}</label>
                  <textarea
                    value={domainNotes[d.id] || ''}
                    onChange={(e) =>
                      setDomainNotes((prev) => ({
                        ...prev,
                        [d.id]: e.target.value,
                      }))
                    }
                    className="w-full border px-3 py-2 rounded"
                    rows={4}
                  />
                </div>
              ))
            )}

            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => setShowNotesModal(false)} className="px-4 py-2 bg-gray-300 rounded">
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                disabled={notesSaving || notesModalLoading}
                className={`px-4 py-2 bg-blue-600 text-white rounded ${
                  notesSaving || notesModalLoading ? 'opacity-50 pointer-events-none' : ''
                }`}
              >
                {notesSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR modal */}
      {qrModalVisitId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md text-center">
            <h2 className="text-lg font-semibold mb-4">Parent Signature – G-sign Confirmation</h2>
            {visitConfirmed ? (
              <div className="text-green-600 text-xl font-bold">✅ Visit Confirmed</div>
            ) : (
              <>
                <p className="mb-4">Scan this QR code with the parent's device:</p>
                <div className="inline-block bg-white p-4 shadow rounded mx-auto">
                  <QRCode value={`${window.location.origin}/visit/${qrModalVisitId}/confirm`} size={192} />
                </div>
                <p className="mt-4 text-sm text-gray-600 break-all">{`${window.location.origin}/visit/${qrModalVisitId}/confirm`}</p>
              </>
            )}
            <button onClick={handleCloseQRModal} className="mt-6 bg-gray-600 text-white px-4 py-2 rounded">
              Close
            </button>
          </div>
        </div>
      )}

      <CancelModal
        open={!!cancelVisitId}
        onClose={() => setCancelVisitId(null)}
        onSubmit={handleCancelVisit}
        loading={cancelLoading}
      />

      <CancelWorkModal
        open={!!cancelWorkId}
        onClose={() => setCancelWorkId(null)}
        onSubmit={handleCancelWork}
        loading={cancelWorkLoading}
      />

      <WorkLocationModal
        open={!!workLocModal?.open}
        onClose={() => setWorkLocModal({ open: false, title: '', addressLine: '', dest: null, teacher: null })}
        title={workLocModal?.title}
        addressLine={workLocModal?.addressLine}
        dest={workLocModal?.dest}
        teacher={workLocModal?.teacher}
      />

      <StudentDetailsModal
        open={studentDetailsModal.open}
        onClose={() => setStudentDetailsModal({ open: false, student: null })}
        student={studentDetailsModal.student}
      />

      <MapModal
        open={mapModal.open}
        onClose={() => setMapModal({ open: false, student: null, teacher: null })}
        student={mapModal.student}
        teacher={mapModal.teacher}
      />

      <MentorFeedbackModal
        open={mentorFeedback.open}
        visit={mentorFeedback.visit}
        onClose={() => setMentorFeedback({ open: false, visit: null })}
      />

      {/* ✅ Work Notes Modal (same UI pattern as admin) */}
      {workNotesModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="text-lg font-bold truncate">Work Notes — {workNotesModal.workType}</h3>
                <div className="text-xs text-gray-500 mt-1">
                  {workNotesModal.teacherName ? `Teacher: ${workNotesModal.teacherName}` : null}
                  {workNotesModal.scheduledAt
                    ? ` • ${String(workNotesModal.scheduledAt).slice(0, 16).replace('T', ' ')}`
                    : null}
                </div>
              </div>
              <button className="text-gray-500" onClick={() => setWorkNotesModal((s) => ({ ...s, open: false }))}>
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div className="border rounded p-3 bg-gray-50">
                <div className="text-xs text-gray-500 mb-1">Title</div>
                <div className="font-semibold text-slate-800">
                  {workNotesModal.workTitle || <span className="italic text-gray-400">No title</span>}
                </div>
              </div>

              <div className="border rounded p-3 bg-gray-50">
                <div className="text-xs text-gray-500 mb-1">Notes</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">
                  {workNotesModal.notes || <span className="italic text-gray-400">No notes</span>}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setWorkNotesModal((s) => ({ ...s, open: false }))}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">Notes History: {notesHistoryStudent}</h3>
              <button className="text-gray-500" onClick={() => setShowNotesHistory(false)}>
                ✕
              </button>
            </div>
            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">Loading...</div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No notes found for this student.</div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex flex-col">
                        <span className="font-semibold text-blue-900">{entry.teacher_name}</span>
                        {entry.discipline_name && <span className="text-xs text-gray-500">{entry.discipline_name}</span>}
                      </div>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at ? entry.scheduled_at.slice(0, 16).replace('T', ' ') : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {entry.domains && entry.domains.length > 0 ? (
                        entry.domains.map((dom) =>
                          dom.note && dom.note.trim() !== '' ? (
                            <div key={dom.domain_id}>
                              <span className="font-semibold">{dom.name || dom.code || 'Domain'}:</span>{' '}
                              <span>{dom.note}</span>
                            </div>
                          ) : null
                        )
                      ) : (
                        <span className="text-xs text-gray-500 italic">No domain notes for this visit.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MyVisits;













